import { KycCheckpointId, KYCStatus } from '../../../enums';
import { useKycMeByOriginIdWithOrigin } from '../useKycMeByOriginIdWithOrigin';
import { UseKycInformationHandlerProps } from './props';

/**
 * Custom hook to handle KYC information.
 * This hook provides functionality to retrieve and manage KYC information based on the user's origin ID.
 *
 * @param {UseKycInformationHandlerProps} [props] - Optional properties for handling KYC information.
 * @returns {Object} - An object containing the KYC query, lottie animation, title, and description.
 *
 * Example usage:
 * const { useKycMeByOriginIdQuery, lottie, title, description } = useKycInformationHandler({
 *   externalOriginId: '123',
 *   externalEnabled: true,
 * });
 */
export function useKycInformationHandler(
  props?: UseKycInformationHandlerProps,
) {
  // Hook to fetch KYC information by origin ID
  const useKycMeByOriginIdQuery = useKycMeByOriginIdWithOrigin({
    externalOriginId: props?.externalOriginId,
    enabled: props?.externalEnabled,
  });

  // Destructure the KYC data from the query result
  const { kyc } = useKycMeByOriginIdQuery;

  /**
   * Function to get the title based on KYC status and checkpoint.
   *
   * @returns {string} - The title corresponding to the current KYC status and checkpoint.
   *
   * Example:
   * const title = getTitle();
   */
  function getTitle(): string {
    if (!kyc) {
      return 'create_personal_identity_title';
    }

    if (kyc.status === KYCStatus.Created) {
      switch (kyc?.stage?.checkpoint?.id) {
        case KycCheckpointId.AddressVerification:
          return 'common:address_verification_label';
        case KycCheckpointId.CriminalRecordCheck:
          return 'common:criminal_record_check_label';
        case KycCheckpointId.DocumentSubmission:
          return 'common:document_submission_label';
        case KycCheckpointId.Identity:
          return 'common:identity_label';
        case KycCheckpointId.VerifiedTaxId:
          return 'common:verified_tax_id_label';
        case KycCheckpointId.ProofOfResidence:
          return 'common:proof_of_residence_label';
        case KycCheckpointId.ProofOfIncome:
          return 'common:proof_of_income_label';
        case KycCheckpointId.VerifiedEmail:
          return 'common:verified_email_label';
        case KycCheckpointId.VerifiedPhoneNumber:
          return 'common:verified_phone_number_label';
        default:
          return 'created_personal_identity_title';
      }
    }
  }

  /**
   * Function to get the description based on KYC status and checkpoint.
   *
   * @returns {string} - The description corresponding to the current KYC status and checkpoint.
   *
   * Example:
   * const description = getDescription();
   */
  function getDescription(): string {
    if (!kyc) {
      return 'create_personal_identity_description';
    }

    if (kyc?.stage?.checkpoint?.id === KycCheckpointId.DocumentSubmission) {
      return 'document_submission_description';
    }

    if (kyc.status === KYCStatus.Created) {
      return 'create_personal_identity_description';
    }

    return 'created_personal_identity_description';
  }

  /**
   * Function to get the Lottie animation file based on KYC status.
   *
   * @returns {string} - The Lottie animation file name.
   *
   * Example:
   * const lottie = getLottie();
   */
  function getLottie(): string {
    if (!kyc || (!!kyc && kyc?.status === KYCStatus.Verified)) {
      return 'kyc-pending-form.json';
    }

    return 'identity-check.json';
  }

  // Return the KYC query, lottie animation, title, and description
  return {
    useKycMeByOriginIdQuery,
    lottie: getLottie(),
    title: getTitle(),
    description: getDescription(),
  };
}
